import ReactECharts from 'echarts-for-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { RampCurveEntry } from '@/features/resource/types'
import { convertToKilo } from '@/features/resource/utils/convertToKilo'

interface RampCurveChartProps {
  entries: RampCurveEntry[]
}

interface PinnedPoint {
  dataIndex: number
  value: [number, number]
  entryNumber: number
}

const STYLES = {
  pinnedTooltip: {
    position: 'absolute' as const,
    background: 'rgba(0, 0, 0, 0.85)',
    color: 'white',
    padding: '8px 10px',
    borderRadius: '4px',
    border: '2px solid #20a4a0',
    fontSize: '11px',
    lineHeight: '18px',
    pointerEvents: 'none' as const,
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap' as const,
  },
  controlPanel: {
    position: 'absolute' as const,
    bottom: 50,
    right: -30,
    background: 'white',
    padding: '8px 12px',
    borderRadius: 4,
    border: '1px solid #ddd',
    fontSize: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    zIndex: 1002,
  },
  clearButton: {
    background: '#20a4a0',
    color: 'white',
    border: 'none',
    padding: '4px 8px',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: '11px',
    transition: 'background 0.2s',
  },
  clearButtonHover: {
    background: '#1a8a86',
  },
} as const

const RampCurveChart = ({ entries }: RampCurveChartProps) => {
  const { t } = useTranslation()
  const chartRef = useRef<ReactECharts>(null)
  const [pinnedPoints, setPinnedPoints] = useState<PinnedPoint[]>([])
  const [hoveredButton, setHoveredButton] = useState(false)
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 100 })

  const sortedData = useMemo(() => {
    if (!entries?.length) return []

    return [...entries]
      .sort((a, b) => a.durationSinceStartMillis - b.durationSinceStartMillis)
      .map((entry, index) => ({
        name: `${index + 1}`,
        value: [entry.durationSinceStartMillis, convertToKilo(entry.consumption) ?? 0],
        dataIndex: index,
      }))
  }, [entries])

  const datasetSize = useMemo(
    () => ({
      isLarge: entries.length > 30,
      isMedium: entries.length > 10,
    }),
    [entries.length],
  )

  const togglePin = useCallback((params: any) => {
    if (params.componentType !== 'series' || params.dataIndex === undefined) return

    const { dataIndex, data } = params

    setPinnedPoints((prev) => {
      const isPinned = prev.some((p) => p.dataIndex === dataIndex)
      return isPinned
        ? prev.filter((p) => p.dataIndex !== dataIndex)
        : [...prev, { dataIndex, value: data.value, entryNumber: dataIndex + 1 }]
    })
  }, [])

  const getSymbolSize = useCallback((datasetSize: { isLarge: boolean; isMedium: boolean }) => {
    if (datasetSize.isLarge) return 0
    if (datasetSize.isMedium) return 8
    return 12
  }, [])

  const chartOption = useMemo(
    () => ({
      title: {
        text: t('resources.ramp_curves.chart.title'),
        left: 'left',
        textStyle: { fontSize: 16, fontWeight: 'normal', color: '#333' },
      },
      grid: {
        left: '12%',
        right: '10%',
        bottom: datasetSize.isLarge ? '20%' : '15%',
        top: '15%',
      },
      ...(datasetSize.isLarge && {
        dataZoom: [
          {
            show: true,
            realtime: true,
            start: 0,
            end: 100,
            bottom: '1%',
          },
        ],
      }),
      tooltip: {
        trigger: datasetSize.isLarge ? 'axis' : 'item',
        formatter: (params: any) => {
          const point = Array.isArray(params) ? params[0] : params
          if (!point?.data) return ''
          const isPinned = pinnedPoints.some((p) => p.dataIndex === point.dataIndex)
          return (
            `${isPinned ? '📌 ' : ''}Entry ${point.dataIndex + 1}<br/>` +
            `Time: ${point.data.value[0]} ms<br/>` +
            `Consumption: ${point.data.value[1].toFixed(2)} kW`
          )
        },
        enterable: true,
        confine: true,
      },
      xAxis: {
        type: 'value',
        name: t('resources.ramp_curves.chart.time_axis_ms'),
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { color: '#999', fontSize: 12 },
        axisLabel: { color: '#999' },
        axisLine: { lineStyle: { color: '#ddd' } },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      yAxis: {
        type: 'value',
        name: t('resources.ramp_curves.chart.consumption_axis'),
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: { color: '#999', fontSize: 12 },
        axisLabel: { color: '#999' },
        axisLine: { lineStyle: { color: '#ddd' } },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        {
          type: 'line',
          data: sortedData,
          lineStyle: {
            color: '#20a4a0',
            width: datasetSize.isLarge ? 2 : 3,
          },
          itemStyle: {
            color: '#20a4a0',
            borderColor: '#20a4a0',
            borderWidth: 2,
          },
          symbol: 'circle',
          symbolSize: getSymbolSize(datasetSize),
          emphasis: {
            disabled: false,
            symbolSize: 8,
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 10,
              shadowColor: 'rgba(32, 164, 160, 0.5)',
            },
          },
          label: {
            show: !datasetSize.isLarge,
            position: 'top',
            formatter: '{b}',
            color: '#333',
            fontSize: 12,
            offset: [0, -10],
          },
          smooth: !datasetSize.isLarge,
          animation: false,
          triggerLineEvent: true,
        },
      ],
    }),
    [sortedData, datasetSize, t, getSymbolSize],
  )

  useEffect(() => {
    if (!datasetSize.isLarge) return

    const chart = chartRef.current?.getEchartsInstance()
    if (!chart) return

    const startIdx = Math.floor((visibleRange.start / 100) * sortedData.length)
    const endIdx = Math.ceil((visibleRange.end / 100) * sortedData.length)
    const visiblePointsCount = endIdx - startIdx

    const showLabels = visiblePointsCount <= 30
    let symbolSize = 0
    if (visiblePointsCount <= 10) {
      symbolSize = 12
    } else if (visiblePointsCount <= 30) {
      symbolSize = 8
    }

    const smooth = visiblePointsCount <= 50

    chart.setOption(
      {
        series: [
          {
            symbolSize,
            label: {
              show: showLabels,
            },
            smooth,
            triggerLineEvent: true,
          },
        ],
      },
      {
        notMerge: false,
        lazyUpdate: true,
        replaceMerge: [],
      },
    )
  }, [visibleRange, sortedData.length, datasetSize.isLarge])

  const handleDataZoom = useCallback((params: any) => {
    if (params?.start == null || params?.end == null) return
    setVisibleRange({ start: params.start, end: params.end })
  }, [])

  const getTooltipPosition = useCallback((point: PinnedPoint) => {
    const chart = chartRef.current?.getEchartsInstance()
    if (!chart) return null

    const pos = chart.convertToPixel('grid', point.value)
    if (!pos || !Array.isArray(pos) || pos.length < 2 || !Number.isFinite(pos[0]) || !Number.isFinite(pos[1]))
      return null

    return {
      left: `${pos[0]}px`,
      top: `${Math.max(0, pos[1] - 80)}px`,
    }
  }, [])

  const clearPins = useCallback(() => {
    setPinnedPoints([])
  }, [])

  if (!entries?.length) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
        {t('resources.ramp_curves.chart.no_data', 'No data available')}
      </div>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      <ReactECharts
        ref={chartRef}
        option={chartOption}
        opts={{ renderer: datasetSize.isLarge ? 'canvas' : 'svg' }}
        style={{ height: '400px', width: '100%' }}
        onEvents={{
          click: togglePin,
          ...(datasetSize.isLarge && { dataZoom: handleDataZoom }),
        }}
      />

      {pinnedPoints.map((point) => {
        const position = getTooltipPosition(point)
        return (
          position && (
            <div key={`pin-${point.dataIndex}`} style={{ ...STYLES.pinnedTooltip, ...position }}>
              <div>📌 Entry {point.entryNumber}</div>
              <div>Time: {point.value[0]} ms</div>
              <div>Consumption: {point.value[1].toFixed(2)} kW</div>
            </div>
          )
        )
      })}

      {pinnedPoints.length > 0 && (
        <div style={STYLES.controlPanel}>
          <div style={{ marginBottom: 4, fontWeight: 500, color: '#333' }}>📌 {pinnedPoints.length} pinned</div>
          <button
            style={{
              ...STYLES.clearButton,
              ...(hoveredButton ? STYLES.clearButtonHover : {}),
            }}
            onClick={clearPins}
            onMouseEnter={() => setHoveredButton(true)}
            onMouseLeave={() => setHoveredButton(false)}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  )
}

export default RampCurveChart
